import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Trophy, Star, Pin, Eye, Users, Vote, Loader2, Megaphone, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import DashboardHeroToggle from "@/components/DashboardHeroToggle";

function resolveImage(p) {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  if (p.startsWith("/api/")) return p;
  return `${API}/img/${p}?w=1600&q=86`;
}

export default function AdminConcoursV2() {
  const [items, setItems] = useState([]);
  const [statsMap, setStatsMap] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/concours");
      const list = Array.isArray(data) ? data : [];
      setItems(list);
      // fetch stats in parallel
      const sm = {};
      await Promise.all(list.map(async (c) => {
        try {
          const { data: s } = await api.get(`/admin/concours/${c.id}/stats`);
          sm[c.id] = s;
        } catch { sm[c.id] = null; }
      }));
      setStatsMap(sm);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const patchMeta = async (cid, patch) => {
    try {
      await api.patch(`/admin/concours/${cid}/featured`, patch);
      toast.success("Mis à jour");
      // optimistic refresh of stats
      const { data: s } = await api.get(`/admin/concours/${cid}/stats`);
      setStatsMap((prev) => ({ ...prev, [cid]: s }));
      setItems((prev) => prev.map((c) => (c.id === cid ? { ...c, ...patch } : c)));
    } catch (e) {
      toast.error(e?.userMessage || "Échec");
    }
  };

  const pinEverywhere = async (cid) => {
    if (!window.confirm("Mettre ce concours en avant sur TOUTES les surfaces (accueil, dashboards talent & créateur, événements) ?")) return;
    try {
      const slots = ["homepage", "talent_dashboard", "creator_dashboard", "events_section"];
      await Promise.all(slots.map((slot) =>
        api.post("/admin/featured", { kind: "concours", target_id: cid, slot, active: true })
      ));
      await patchMeta(cid, { pinned_everywhere: true, featured: true });
      toast.success("Concours épinglé partout");
    } catch (e) {
      toast.error(e?.userMessage || "Échec");
    }
  };

  return (
    <div data-testid="admin-concours-v2-page">
        <div className="mb-8">
          <p className="caption-label text-[#A26F3E]">Curation</p>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-1 leading-[1.05]">Concours</h1>
          <p className="text-[#666] mt-2">Pilotez les concours, leurs statistiques et leur mise en avant.</p>
        </div>

        {loading ? (
          <div className="py-24 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-[#C68E59] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="card-lux p-12 text-center" data-testid="concours-v2-empty">
            <Trophy className="w-8 h-8 text-[#C68E59] mx-auto" />
            <p className="font-serif text-2xl text-[#1A1A1A] mt-3">Aucun concours pour l'instant</p>
            <p className="text-sm text-[#666] mt-1">Créez votre premier concours depuis l'écran "Concours".</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="concours-v2-list">
            {items.map((c) => (
              <ConcoursRow
                key={c.id}
                concours={c}
                stats={statsMap[c.id]}
                onPatchMeta={patchMeta}
                onPinEverywhere={pinEverywhere}
              />
            ))}
          </div>
        )}
      </div>
  );
}

function ConcoursRow({ concours, stats, onPatchMeta, onPinEverywhere }) {
  const c = concours;
  const s = stats || {};
  const closed = c.status === "closed";
  return (
    <div className="card-lux p-5 sm:p-6" data-testid={`concours-row-${c.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-4 flex gap-4">
          {c.image_url ? (
            <img src={resolveImage(c.image_url)} alt="" className="w-24 h-24 rounded-2xl object-cover border border-[#EADDC9] flex-shrink-0" />
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-[#FAF3EA] flex items-center justify-center border border-[#EADDC9] flex-shrink-0">
              <Trophy className="w-7 h-7 text-[#C68E59]" />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {c.featured && (
                <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#F4E4C9] text-[#8B5A2B]" data-testid="badge-featured">
                  En vedette
                </span>
              )}
              {c.pinned_everywhere && (
                <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#1A1A1A] text-white" data-testid="badge-pinned">
                  Épinglé partout
                </span>
              )}
              <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${closed ? "bg-[#EADDC9] text-[#666]" : "bg-[#E9EFD7] text-[#3A4A1F]"}`}>
                {closed ? "Clôturé" : "Actif"}
              </span>
            </div>
            <p className="font-serif text-lg text-[#1A1A1A] mt-2 leading-tight">{c.title}</p>
            {c.theme && <p className="text-xs text-[#8B8680] mt-0.5">{c.theme}</p>}
            <div className="flex items-center gap-3 mt-2 text-xs text-[#8B8680]">
              <span>{c.start_date?.slice(0, 10)} → {c.end_date?.slice(0, 10)}</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="lg:col-span-4 grid grid-cols-4 gap-2">
          <Stat icon={Users}  label="Particip." value={s.participants ?? "—"} />
          <Stat icon={Vote}   label="Votes"     value={s.votes ?? "—"} />
          <Stat icon={Eye}    label="Vues"      value={s.views ?? "—"} />
          <Stat icon={Star}   label="Approuvés" value={s.approved ?? "—"} />
        </div>

        {/* Actions */}
        <div className="lg:col-span-4 flex flex-col gap-2">
          <ToggleRow
            label="En vedette"
            checked={!!c.featured}
            onChange={(v) => onPatchMeta(c.id, { featured: v })}
            testid={`toggle-featured-${c.id}`}
          />
          <ToggleRow
            label="Clôture automatique à la date de fin"
            checked={!!c.auto_close}
            onChange={(v) => onPatchMeta(c.id, { auto_close: v })}
            testid={`toggle-auto-close-${c.id}`}
          />
          <ToggleRow
            label="Épinglé partout"
            checked={!!c.pinned_everywhere}
            onChange={(v) => onPatchMeta(c.id, { pinned_everywhere: v })}
            testid={`toggle-pinned-${c.id}`}
          />
          <Button
            onClick={() => onPinEverywhere(c.id)}
            className="rounded-full bg-[#1A1A1A] hover:bg-[#2A1E12] text-white text-xs h-9 mt-1"
            data-testid={`btn-pin-everywhere-${c.id}`}
          >
            <Megaphone className="w-3.5 h-3.5 mr-1.5" /> Mettre en avant partout
          </Button>
          <div className="pt-2 mt-1 border-t border-[#FAF3EA]">
            <DashboardHeroToggle kind="contest" targetId={c.id} compact />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-[#FAF3EA] border border-[#EADDC9] p-3">
      <Icon className="w-3.5 h-3.5 text-[#C68E59]" />
      <p className="font-serif text-xl text-[#1A1A1A] mt-1 leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-[#8B8680] mt-0.5">{label}</p>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white border border-[#EADDC9] cursor-pointer">
      <span className="text-xs text-[#1A1A1A]">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}
